import React, { useEffect, useMemo, useState } from "react";
import PlanSummary from "../components/PlanSummary";
import { logUiEvent } from "../services/analytics";
import { createCheckoutSession } from "../services/functionsClient";

const plans = [
  {
    name: "Starter",
    price: "$20/mo",
    features: [
      "Single model included",
      "Internal hosting",
      "Basic analytics",
      "Limited exports",
      "Email support"
    ]
  },
  {
    name: "Growth",
    price: "$50/mo",
    features: [
      "Everything in previous plan plus...",
      "Additional models",
      "Ability to export",
      "Add external agents and MCP servers",
      "Log access"
    ]
  },
  {
    name: "Enterprise",
    price: "Talk to us",
    features: [
      "Everything in previous plan plus...",
      "SAML/SCIM",
      "Dedicated regions",
      "Custom model registry",
      "Premium support"
    ]
  }
];

const priceIds = {
  Starter: process.env.REACT_APP_STRIPE_STARTER_PRICE_ID,
  Growth: process.env.REACT_APP_STRIPE_GROWTH_PRICE_ID
};

function PlanPage({ user }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [signupStarted, setSignupStarted] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (selectedPlan) {
      setError("");
    }
  }, [selectedPlan]);

  const planCtaText = useMemo(() => {
    if (!selectedPlan) return "Pick a plan to start";
    return `Ready to start your ${selectedPlan.name} signup?`;
  }, [selectedPlan]);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setSignupStarted(false);
    logUiEvent("plan_select", { plan: plan.name });
  };

  const handleStartCheckout = async () => {
    if (!selectedPlan) {
      setError("Pick a plan to continue");
      return;
    }

    logUiEvent("plan_signup", { plan: selectedPlan.name });
    setSignupStarted(false);
    setError("");

    const selectedPrice = priceIds[selectedPlan.name];
    if (!selectedPrice) {
      setError("Stripe price ID missing. Set REACT_APP_STRIPE_STARTER_PRICE_ID and REACT_APP_STRIPE_GROWTH_PRICE_ID.");
      return;
    }

    setCheckoutLoading(true);

    try {
      const { url } = await createCheckoutSession({
        plan: selectedPlan.name,
        userId: user?.uid,
        email: user?.email,
        successUrl: `${window.location.origin}/plan?checkout=success`,
        cancelUrl: `${window.location.origin}/plan?checkout=cancelled`
      });

      setSignupStarted(true);
      window.location.href = url;
    } catch (err) {
      setError(err?.message || "Failed to start checkout.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="grid" style={{ gap: "16px" }}>
      <PlanSummary plan={{ name: "Starter", monthlyCredits: 25, llmModel: "gpt-5-mini-2025-08-07", maxApps: 5, maxSnapshots: 20 }} />

      <div className="panel">
        <h3 className="section-title">Choose a plan</h3>
        <div className="grid grid--two">
          {plans.map((plan) => (
            <div key={plan.name} className="card">
              <h4 className="card__title">{plan.name}</h4>
              <div className="card__meta">{plan.price}</div>
              <ul className="list">
                {plan.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              <button
                className="button"
                style={{ marginTop: "14px" }}
                onClick={() => handleSelectPlan(plan)}
                type="button"
              >
                Select
              </button>
            </div>
          ))}
        </div>
        <div className="panel" style={{ marginTop: "16px" }}>
          <h4 className="section-title">Plan sign up</h4>
          <p style={{ marginBottom: "12px" }}>{planCtaText}</p>
          <div className="grid" style={{ gridTemplateColumns: "1fr", gap: "8px" }}>
            <button
              className="button primary"
              type="button"
              onClick={handleStartCheckout}
              disabled={!selectedPlan || checkoutLoading}
            >
              {checkoutLoading ? "Starting checkout..." : "Continue to sign up flow"}
            </button>
            <p className="muted" style={{ margin: 0 }}>
              You will be redirected to Stripe Checkout for the selected plan. Billing updates will reflect after payment
              succeeds.
            </p>
            {error && (
              <div className="card" style={{ background: "#fff6f6", border: "1px solid #f5c2c2" }}>
                <p style={{ margin: 0 }}>
                  {error}
                </p>
              </div>
            )}
            {signupStarted && selectedPlan && (
              <div className="card" style={{ marginTop: "4px" }}>
                <h5 className="card__title">We heard you!</h5>
                <p style={{ marginBottom: 0 }}>
                  We'll route you to the {selectedPlan.name} checkout shortly. If nothing happens, double-check your Stripe
                  configuration or try again.
                </p>
              </div>
            )}
          </div>
        </div>
        <p className="muted" style={{ marginTop: "12px" }}>
          Payments run through Stripe. Attach your publishable key and webhook secret in the Firebase Function
          environment to enable real payments.
        </p>
      </div>
    </div>
  );
}

export default PlanPage;
